/**
 * SARKY Storefront Wishlist -- shared client state module.
 *
 * Browser-local (see the round's final report for the full native-vs-web
 * architecture audit -- this is a deliberate, documented choice, not a fake
 * "synced" wishlist).
 *
 * Storage key: sarkyWishlist:v1 (unchanged -- only the payload SHAPE inside
 * it is versioned, via the `version` field below; the key itself never
 * needed to change).
 *
 * PRESERVE SAVED WISHLIST VARIANT / SIZE (this pass): schema v2.
 *   { version: 2, updatedAt: <ISO string>, items: [{ handle, variantId }] }
 * Each item is a Shopify PRODUCT HANDLE (unchanged reason: zero-network
 * card/PDP heart state, tokenless `/products/{handle}.js` resolution) PLUS
 * the specific numeric variant id the visitor had selected when they saved
 * it -- `variantId` is `null` when no specific variant is known (e.g. a
 * single-variant product, or an old entry that predates this schema).
 * `null` is a real, permanent state here, not just a migration artifact:
 * it means "let Shopify's own selected_or_first_available_variant decide",
 * the exact same safe default every product-card and PDP already falls
 * back to when no variant is specified.
 *
 * MIGRATION: schema v1 (`{ version: 1, productIds: string[] }`, the format
 * every wishlist saved before this pass) is read transparently and
 * converted in memory to v2 items with `variantId: null` -- existing saved
 * products are never dropped, and the very next write persists them in v2
 * so the migration only ever has to happen once per browser. No separate
 * "migration routine" is invoked anywhere else; readStored() is the single
 * place old and new data both funnel through.
 *
 * No framework, no dependency. A single delegated click listener on
 * `document` wires every [data-sarky-wishlist-heart] button on the page,
 * including ones added to the DOM later (product cards loaded via infinite
 * scroll, the Wishlist page's own dynamically rendered cards, etc.).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sarkyWishlist:v1';
  var STORAGE_VERSION = 2;
  var MAX_ITEMS = 100;
  var CHANGE_EVENT = 'sarky:wishlist:change';

  function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  function normalizeVariantId(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    var num = Number(value);

    return Number.isFinite(num) && num > 0 ? num : null;
  }

  // Dedupe-and-cap pass shared by both the v1->v2 migration and a native v2
  // read, so the two paths can never drift into different sanitization
  // rules for the same underlying data.
  function sanitizeItems(rawItems) {
    var unique = [];
    var seen = {};

    for (var i = 0; i < rawItems.length; i++) {
      var candidate = rawItems[i];
      var handle = candidate && typeof candidate.handle === 'string' ? candidate.handle.trim() : '';

      if (handle === '' || seen[handle]) {
        continue;
      }

      seen[handle] = true;
      unique.push({ handle: handle, variantId: normalizeVariantId(candidate.variantId) });
    }

    return unique.slice(0, MAX_ITEMS);
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);

      if (raw === null) {
        return [];
      }

      var parsed = JSON.parse(raw);

      if (!isRecord(parsed)) {
        return [];
      }

      // v1 (handle-only) -- migrate in memory. The caller (writeStored, via
      // any subsequent add/remove/toggle) persists this back as v2 on the
      // very next write; nothing here writes to storage itself, so a
      // read-only page load never mutates a v1 visitor's data.
      if (parsed.version === 1 && Array.isArray(parsed.productIds)) {
        return sanitizeItems(
          parsed.productIds.map(function (handle) {
            return { handle: handle, variantId: null };
          })
        );
      }

      if (parsed.version === STORAGE_VERSION && Array.isArray(parsed.items)) {
        return sanitizeItems(parsed.items);
      }

      return [];
    } catch (error) {
      // Malformed JSON, storage unavailable (private mode, quota, etc.) --
      // fall back to an empty wishlist rather than crashing the page.
      return [];
    }
  }

  function writeStored(items) {
    try {
      var payload = {
        version: STORAGE_VERSION,
        updatedAt: new Date().toISOString(),
        items: items,
      };

      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // Best-effort only -- the in-memory state (currentItems) still
      // reflects this session's choice even if it can't be persisted.
    }
  }

  var currentItems = readStored();

  function notify(source) {
    try {
      window.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: {
            productIds: currentItems.map(function (item) {
              return item.handle;
            }),
            items: currentItems.slice(),
            source: source || 'local',
          },
        })
      );
    } catch (error) {
      // No CustomEvent support: storage is still correct, only live
      // same-page sync is skipped.
    }
  }

  function findItem(handle) {
    for (var i = 0; i < currentItems.length; i++) {
      if (currentItems[i].handle === handle) {
        return currentItems[i];
      }
    }

    return null;
  }

  function isWishlisted(handle) {
    return findItem(handle) !== null;
  }

  function getVariantId(handle) {
    var item = findItem(handle);

    return item ? item.variantId : null;
  }

  function add(handle, variantId) {
    if (!handle || isWishlisted(handle) || currentItems.length >= MAX_ITEMS) {
      return;
    }

    currentItems = currentItems.concat([{ handle: handle, variantId: normalizeVariantId(variantId) }]);
    writeStored(currentItems);
    notify('local');
  }

  function remove(handle) {
    if (!handle || !isWishlisted(handle)) {
      return;
    }

    currentItems = currentItems.filter(function (item) {
      return item.handle !== handle;
    });
    writeStored(currentItems);
    notify('local');
  }

  function toggle(handle, variantId) {
    if (isWishlisted(handle)) {
      remove(handle);
    } else {
      add(handle, variantId);
    }
  }

  // Updates the saved variant for an ALREADY-wishlisted item -- e.g. the
  // visitor picks a different color on that item's own Wishlist card and
  // Shopify's own variant-picker resolves a new variant (requirement 4).
  // Never adds a new item and never removes one; a handle that isn't
  // currently wishlisted is a no-op, matching add()/remove()'s own guards.
  function updateVariant(handle, variantId) {
    var item = findItem(handle);
    var normalized = normalizeVariantId(variantId);

    if (!item || item.variantId === normalized) {
      return;
    }

    item.variantId = normalized;
    writeStored(currentItems);
    notify('local');
  }

  function getAll() {
    return currentItems.map(function (item) {
      return item.handle;
    });
  }

  function getAllItems() {
    return currentItems.slice();
  }

  function setHeartState(button, saved) {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');

    var label = saved ? button.getAttribute('data-label-remove') : button.getAttribute('data-label-add');

    if (label) {
      button.setAttribute('aria-label', label);
    }
  }

  function syncAllHearts(root) {
    var scope = root || document;
    var hearts = scope.querySelectorAll('[data-sarky-wishlist-heart]');

    for (var i = 0; i < hearts.length; i++) {
      var heart = hearts[i];
      var handle = heart.getAttribute('data-product-handle');

      if (handle) {
        setHeartState(heart, isWishlisted(handle));
      }
    }
  }

  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function playPop(button) {
    if (reducedMotionQuery && reducedMotionQuery.matches) {
      return;
    }

    button.classList.remove('is-animating');
    // Force reflow so rapid re-toggles restart the animation instead of
    // silently no-opping because the class never actually left.
    void button.offsetWidth;
    button.classList.add('is-animating');
  }

  // Cross-tab sync: the native `storage` event only fires in OTHER open
  // tabs when localStorage changes there -- exactly the multi-tab behavior
  // asked for, with zero extra library.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    currentItems = readStored();
    notify('storage');
    syncAllHearts();
  });

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-sarky-wishlist-heart]') : null;

    if (!button) {
      return;
    }

    // Hearts always sit inside a card/media link that would otherwise
    // navigate to the PDP -- stop that unconditionally.
    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-product-handle');

    if (!handle) {
      return;
    }

    // SARKY wishlist-sheet (assets/sarky-wishlist-sheet.js): a not-yet-saved
    // heart opens the compact sheet (photo/name/price/size context, then a
    // real "add" action inside it) instead of saving instantly. An
    // already-saved heart, and any dedicated "remove" control (e.g. the
    // Wishlist page's own cards), keep removing instantly exactly as
    // before -- undoing a save should never require reopening a sheet, and
    // this must never change what the Wishlist page's existing items do.
    if (
      !isWishlisted(handle) &&
      !button.hasAttribute('data-sarky-wishlist-remove') &&
      window.SarkyWishlistSheet &&
      window.SarkyWishlistSheet.open(handle, button)
    ) {
      return;
    }

    toggle(handle);
    syncAllHearts();
    playPop(button);

    if (button.hasAttribute('data-sarky-wishlist-remove')) {
      var item = button.closest('[data-sarky-wishlist-item]');
      var grid = item ? item.parentElement : null;

      if (item) {
        item.remove();
      }

      // Generic event, not tied to any specific page's markup -- lets the
      // Wishlist page (or anything else showing removable saved items)
      // react to "the list I'm showing just became empty" without this
      // shared module needing to know about that page's own empty-state
      // elements.
      window.dispatchEvent(
        new CustomEvent('sarky:wishlist:item-removed', {
          detail: { productHandle: handle, remaining: grid ? grid.children.length : null },
        })
      );
    }
  });

  document.addEventListener('animationend', function (event) {
    var target = event.target;

    if (target && target.classList && target.classList.contains('sarky-wishlist-heart')) {
      target.classList.remove('is-animating');
    }
  });

  window.addEventListener(CHANGE_EVENT, function () {
    syncAllHearts();
  });

  function init() {
    syncAllHearts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SarkyWishlist = {
    isWishlisted: isWishlisted,
    add: add,
    remove: remove,
    toggle: toggle,
    updateVariant: updateVariant,
    getAll: getAll,
    getAllItems: getAllItems,
    getVariantId: getVariantId,
    syncAllHearts: syncAllHearts,
    playPop: playPop,
    STORAGE_KEY: STORAGE_KEY,
    CHANGE_EVENT: CHANGE_EVENT,
  };

  // Belt-and-suspenders alongside the fixed <head> script placement: any
  // consumer that happens to run before this module has finished
  // (a future theme.liquid edit, an app block, etc.) can listen for this
  // instead of silently finding `window.SarkyWishlist` undefined and
  // giving up -- exactly the bug this event exists to prevent from
  // recurring.
  window.dispatchEvent(new CustomEvent('sarky:wishlist:ready'));
})();
